import React, { useState } from 'react';
import { businessInfo, pricing, facilities, gallery, testimonials, stats, successStories, readingArticles, studyTechniques, thinkerQuotes, readingPathways, studyTools, featuredImage } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Phone,
  MapPin,
  Clock,
  Wifi,
  Car,
  Users,
  Snowflake,
  Bath,
  Volume2,
  Star,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Menu,
  X,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Sparkles,
  Heart,
  Quote,
  Lightbulb,
  GraduationCap,
  Trophy,
  Brain,
  Target
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_496184b7-593b-4859-bb3a-d5c15f25a87d/artifacts/77fcq09t_image.png";

const iconMap = {
  Wifi: Wifi,
  Car: Car,
  Users: Users,
  Snowflake: Snowflake,
  Bath: Bath,
  Volume2: Volume2
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b-4 border-yellow-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-3">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <div>
              <span className="text-2xl font-bold text-indigo-900">MINDSPACE</span>
              <p className="text-xs text-pink-500 font-semibold">Reading Room ✨</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <a href="#about" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg">About</a>
            <a href="#facilities" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg">Facilities</a>
            <a href="#gallery" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg">Gallery</a>
            <a href="#pricing" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg">Pricing</a>
            <a href="#contact" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg">Contact</a>
            <a href="/register" className="text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 px-4 py-2 rounded-full font-bold text-lg transition-all">Register</a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <a href={`tel:${businessInfo.phone}`}>
              <Button className="gap-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full px-6 font-bold shadow-lg">
                <Phone className="w-4 h-4" />
                Call Now!
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-indigo-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t-2 border-yellow-300 bg-white">
            <nav className="flex flex-col gap-4">
              <a href="#about" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg" onClick={() => setIsMenuOpen(false)}>About</a>
              <a href="#facilities" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg" onClick={() => setIsMenuOpen(false)}>Facilities</a>
              <a href="#gallery" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg" onClick={() => setIsMenuOpen(false)}>Gallery</a>
              <a href="#pricing" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg" onClick={() => setIsMenuOpen(false)}>Pricing</a>
              <a href="#contact" className="text-indigo-700 hover:text-pink-500 transition-colors font-bold text-lg" onClick={() => setIsMenuOpen(false)}>Contact</a>
              <a href="/register" className="text-center text-white bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3 rounded-full font-bold text-lg">📝 Register Now</a>
              <a href={`tel:${businessInfo.phone}`}>
                <Button className="w-full gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-bold">
                  <Phone className="w-4 h-4" />
                  Call Now!
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const HeroSection = () => {
  return (
    <section className="pt-28 pb-16 md:pt-36 md:pb-24 bg-gradient-to-br from-yellow-100 via-pink-50 to-cyan-100 relative overflow-hidden">
      {/* Fun decorative shapes */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-60 animate-float"></div>
      <div className="absolute top-40 right-20 w-16 h-16 bg-pink-300 rounded-full opacity-60 animate-float" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-cyan-300 rounded-full opacity-60 animate-float" style={{animationDelay: '0.5s'}}></div>
      <div className="absolute top-1/3 right-10 w-24 h-24 bg-purple-200 rounded-3xl rotate-12 opacity-50"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-white text-indigo-700 rounded-full text-sm font-bold border-3 border-indigo-200 shadow-md">
              <MapPin className="w-5 h-5 text-pink-500" />
              📍 Tirupati, Andhra Pradesh
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-indigo-900 leading-tight">
              {businessInfo.name} 
              <span className="inline-block ml-2 animate-wiggle">📚</span>
            </h1>
            
            <p className="text-lg md:text-xl text-indigo-700 leading-relaxed font-medium">
              {businessInfo.description} 🌟
            </p>

            <div className="flex flex-wrap gap-4">
              <a href={`https://wa.me/91${businessInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gap-2 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white px-8 rounded-full font-bold shadow-lg text-lg">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us 💬
                </Button>
              </a>
              <a href="#contact">
                <Button size="lg" variant="outline" className="gap-2 px-8 border-3 border-indigo-400 text-indigo-700 hover:bg-indigo-50 rounded-full font-bold text-lg">
                  Get Directions
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <Clock className="w-5 h-5 text-pink-500" />
                <span className="font-bold text-indigo-700">{businessInfo.hours.open} - {businessInfo.hours.close}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <Phone className="w-5 h-5 text-pink-500" />
                <span className="font-bold text-indigo-700">{businessInfo.phone}</span>
              </div>
            </div>
          </div>

          {/* Hero Image - Interior Study Room */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white transform rotate-2 hover:rotate-0 transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_496184b7-593b-4859-bb3a-d5c15f25a87d/artifacts/l9p1e66r_image.png"
                alt="MINDSPACE Reading Room Interior - Study Cubicles"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border-4 border-yellow-300 transform -rotate-3">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-indigo-900">36</div>
                  <div className="text-sm text-pink-500 font-bold">Seats! 🪑</div>
                </div>
              </div>
            </div>

            {/* Price Tag */}
            <div className="absolute -top-4 -right-4 bg-gradient-to-br from-yellow-400 to-orange-400 text-indigo-900 rounded-2xl shadow-xl p-4 transform rotate-6">
              <div className="text-sm font-bold">Starting at</div>
              <div className="text-3xl font-bold">₹1,200</div>
              <div className="text-sm font-bold">/month 🎉</div>
            </div>

            {/* Sparkle decoration */}
            <Sparkles className="absolute top-10 left-10 w-8 h-8 text-yellow-400 animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsSection = () => {
  return (
    <section className="py-12 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">{stat.value}</div>
              <div className="text-yellow-200 mt-1 font-bold text-lg">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-yellow-200 rounded-full opacity-50"></div>
      <div className="absolute bottom-10 left-10 w-24 h-24 bg-pink-200 rounded-full opacity-50"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full bg-gradient-to-br from-cyan-300 to-blue-300 rounded-3xl transform rotate-3"></div>
            <img
              src={gallery[3].src}
              alt="Study Area"
              className="relative rounded-3xl shadow-xl w-full h-[400px] object-cover border-4 border-white"
            />
          </div>
          
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-indigo-900">
              Why Choose <span className="text-pink-500">MINDSPACE?</span> 🤔
            </h2>
            <p className="text-lg text-indigo-700 leading-relaxed font-medium">
              Located in the heart of Tirupati, MINDSPACE Reading Room provides the perfect environment 
              for students preparing for competitive exams, college studies, or professional certifications! 📖
            </p>
            <ul className="space-y-4">
              {[
                { text: "Individual cubicles with privacy partitions", emoji: "🔒" },
                { text: "Comfortable seating for long study hours", emoji: "🪑" },
                { text: "Quiet and distraction-free environment", emoji: "🤫" },
                { text: "Affordable monthly plans", emoji: "💰" }
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-pink-50 p-3 rounded-xl">
                  <span className="text-2xl">{item.emoji}</span>
                  <span className="text-indigo-700 font-semibold">{item.text}</span>
                </li>
              ))}
            </ul>
            <a href={`https://wa.me/91${businessInfo.whatsapp}?text=Hi!%20I%20want%20to%20book%20a%20seat%20at%20MINDSPACE%20Reading%20Room`} target="_blank" rel="noopener noreferrer">
              <Button className="gap-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full px-8 font-bold mt-4 shadow-lg">
                <MessageCircle className="w-4 h-4" />
                Book Your Seat Today! 🎯
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Featured Showcase Section with Collage Image
const FeaturedShowcaseSection = () => {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Our <span className="text-yellow-300">Facility</span> at a Glance! 👀
          </h2>
          <p className="text-lg text-pink-100 max-w-2xl mx-auto font-medium">
            A complete study environment designed for your success!
          </p>
        </div>

        {/* Featured Collage Image */}
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/30">
            <img 
              src={featuredImage}
              alt="MINDSPACE Reading Room - Complete Facility Overview"
              className="w-full h-auto object-cover"
            />
          </div>
          
          {/* Floating badges */}
          <div className="absolute -top-4 -left-4 bg-yellow-400 text-indigo-900 px-4 py-2 rounded-full font-bold shadow-lg transform -rotate-6">
            📚 Quiet Zone
          </div>
          <div className="absolute -top-4 -right-4 bg-green-400 text-white px-4 py-2 rounded-full font-bold shadow-lg transform rotate-6">
            ✨ AC Rooms
          </div>
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-pink-500 text-white px-6 py-2 rounded-full font-bold shadow-lg">
            🎯 36 Dedicated Seats
          </div>
        </div>

        {/* Quick highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          {[
            { icon: "🏢", text: "Modern Building" },
            { icon: "❄️", text: "Air Conditioned" },
            { icon: "🪑", text: "Comfy Cubicles" },
            { icon: "🚿", text: "Clean Restrooms" }
          ].map((item, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center">
              <div className="text-3xl mb-2">{item.icon}</div>
              <p className="text-white font-bold text-sm">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FacilitiesSection = () => {
  const funEmojis = ["❄️", "📶", "👥", "🚗", "🚿", "🔇"];
  
  return (
    <section id="facilities" className="py-16 md:py-24 bg-gradient-to-br from-cyan-50 via-white to-yellow-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            Our <span className="text-pink-500">Facilities</span> ✨
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Everything you need for a super productive study session! 🚀
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {facilities.map((facility, index) => {
            const IconComponent = iconMap[facility.icon];
            const colors = [
              "from-pink-400 to-rose-500",
              "from-cyan-400 to-blue-500",
              "from-purple-400 to-indigo-500",
              "from-yellow-400 to-orange-500",
              "from-green-400 to-emerald-500",
              "from-indigo-400 to-purple-500"
            ];
            return (
              <Card key={facility.id} className="group hover:shadow-2xl transition-all duration-300 border-3 border-indigo-100 bg-white rounded-3xl overflow-hidden">
                <CardContent className="p-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${colors[index]} rounded-2xl flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-indigo-900 mb-2">{facility.title} {funEmojis[index]}</h3>
                  <p className="text-indigo-600 font-medium">{facility.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const GallerySection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % gallery.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  return (
    <section id="gallery" className="py-16 md:py-24 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Take a <span className="text-yellow-300">Tour!</span> 📸
          </h2>
          <p className="text-lg text-pink-100 max-w-2xl mx-auto font-medium">
            Explore our awesome reading room facilities!
          </p>
        </div>

        {/* Main Gallery */}
        <div className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
            <img
              src={gallery[currentIndex].src}
              alt={gallery[currentIndex].alt}
              className="w-full h-[300px] md:h-[500px] object-cover transition-all duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-indigo-900/90 to-transparent p-6">
              <p className="text-white text-lg font-bold">{gallery[currentIndex].caption} ✨</p>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white hover:bg-yellow-100 rounded-full flex items-center justify-center shadow-xl transition-all border-3 border-yellow-300"
          >
            <ChevronLeft className="w-7 h-7 text-indigo-700" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white hover:bg-yellow-100 rounded-full flex items-center justify-center shadow-xl transition-all border-3 border-yellow-300"
          >
            <ChevronRight className="w-7 h-7 text-indigo-700" />
          </button>
        </div>

        {/* Thumbnails */}
        <div className="flex justify-center gap-3 mt-6 flex-wrap">
          {gallery.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setCurrentIndex(index)}
              className={`w-20 h-20 rounded-xl overflow-hidden border-3 transition-all ${
                index === currentIndex ? 'border-yellow-300 scale-110 shadow-lg' : 'border-white/50 opacity-70 hover:opacity-100'
              }`}
            >
              <img src={item.src} alt={item.alt} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingSection = () => {
  const planEmojis = ["🌱", "💪", "⭐", "🏆"];
  const planColors = [
    { bg: "from-cyan-400 to-blue-500", border: "border-cyan-300" },
    { bg: "from-green-400 to-emerald-500", border: "border-green-300" },
    { bg: "from-pink-400 to-rose-500", border: "border-pink-300" },
    { bg: "from-yellow-400 to-orange-500", border: "border-yellow-300" }
  ];

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gradient-to-br from-yellow-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            Choose Your <span className="text-pink-500">Plan!</span> 🎯
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Super flexible plans for every student's needs! 🎓
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {pricing.map((plan, index) => (
            <Card 
              key={plan.id} 
              className={`relative bg-white overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 rounded-3xl border-3 ${planColors[index].border} ${
                plan.badge ? "transform scale-105" : ""
              }`}
            >
              {plan.badge && (
                <div className={`bg-gradient-to-r ${plan.badge === "Most Popular" ? "from-pink-500 to-rose-500" : "from-yellow-400 to-orange-500"} text-white text-center py-2 text-sm font-bold uppercase tracking-wide`}>
                  {plan.badge === "Most Popular" ? "⭐ " : "🏆 "}
                  {plan.badge}
                </div>
              )}
              <CardContent className={`p-6 ${!plan.badge ? "pt-8" : ""}`}>
                <div className="text-center mb-6">
                  <div className="text-4xl mb-2">{planEmojis[index]}</div>
                  <h3 className="text-lg font-bold text-indigo-900 mb-1">{plan.name}</h3>
                  <p className="text-sm text-pink-500 font-bold mb-4">{plan.duration}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-4xl font-bold bg-gradient-to-r ${planColors[index].bg} bg-clip-text text-transparent`}>₹{plan.price.toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-indigo-500 mt-1 font-semibold">
                    ₹{plan.perMonth.toLocaleString()} / month
                  </p>
                  {plan.savings && (
                    <div className="mt-2 inline-flex items-center gap-1 px-4 py-1 bg-green-100 text-green-600 rounded-full text-sm font-bold">
                      🎉 Save ₹{plan.savings.toLocaleString()}!
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-indigo-700 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-xs text-pink-500 text-center mb-4 font-bold">
                  👉 {plan.bestFor}
                </p>

                <a href="/register" className="block">
                  <Button 
                    className={`w-full py-5 bg-gradient-to-r ${planColors[index].bg} hover:opacity-90 text-white rounded-full font-bold text-lg shadow-lg gap-2`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    Register Now! 🚀
                  </Button>
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to action */}
        <div className="mt-12 text-center">
          <p className="text-indigo-600 mb-4 font-semibold text-lg">Have questions about our plans? 🤔</p>
          <a href={`tel:${businessInfo.phone}`}>
            <Button variant="outline" className="gap-2 border-3 border-pink-400 text-pink-500 hover:bg-pink-50 rounded-full px-8 font-bold text-lg">
              <Phone className="w-5 h-5" />
              Call {businessInfo.phone} 📞
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            What Students <span className="text-pink-500">Say!</span> 💬
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Hear from our happy students! 😊
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => {
            const colors = ["from-pink-100 to-rose-100", "from-cyan-100 to-blue-100", "from-yellow-100 to-orange-100"];
            return (
              <Card key={testimonial.id} className={`bg-gradient-to-br ${colors[index]} border-3 border-white shadow-lg rounded-3xl hover:shadow-xl transition-shadow`}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-indigo-700 mb-4 font-medium text-lg">"{testimonial.quote}" 💭</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-indigo-900">{testimonial.name}</div>
                      <div className="text-sm text-pink-500 font-semibold">{testimonial.exam}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="text-white space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold">
              Visit <span className="text-yellow-300">Us Today!</span> 🏃‍♂️
            </h2>
            <p className="text-pink-100 text-lg font-medium">
              Come and experience the best study environment in Tirupati! 
              We're here to help you achieve your academic goals! 🎯
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-2xl">
                <div className="w-14 h-14 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <MapPin className="w-7 h-7 text-indigo-900" />
                </div>
                <div>
                  <h3 className="font-bold text-yellow-300 mb-1 text-lg">Address 📍</h3>
                  <p className="text-pink-100 font-medium">
                    {businessInfo.address.line1}<br />
                    {businessInfo.address.line2}<br />
                    {businessInfo.address.city}, {businessInfo.address.state} - {businessInfo.address.pincode}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-2xl">
                <div className="w-14 h-14 bg-pink-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-yellow-300 mb-1 text-lg">Working Hours ⏰</h3>
                  <p className="text-pink-100 font-medium">
                    {businessInfo.hours.open} - {businessInfo.hours.close}<br />
                    Open {businessInfo.hours.days}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-2xl">
                <div className="w-14 h-14 bg-green-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-yellow-300 mb-1 text-lg">Phone 📞</h3>
                  <a href={`tel:${businessInfo.phone}`} className="text-white hover:text-yellow-300 transition-colors text-xl font-bold">
                    {businessInfo.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <a href={`tel:${businessInfo.phone}`}>
                <Button size="lg" className="gap-2 bg-yellow-400 hover:bg-yellow-300 text-indigo-900 rounded-full px-8 font-bold shadow-lg">
                  <Phone className="w-5 h-5" />
                  Call Now! 📱
                </Button>
              </a>
              <a href={`https://wa.me/91${businessInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gap-2 bg-green-400 hover:bg-green-300 text-white rounded-full px-8 font-bold shadow-lg">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp 💬
                </Button>
              </a>
            </div>
          </div>

          {/* Map & Directions */}
          <div className="space-y-4">
            {/* Animated Route Guide */}
            <div className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-5 shadow-xl">
              <h3 className="text-lg md:text-xl font-bold text-indigo-900 mb-3 md:mb-4 flex items-center gap-2">
                <span className="text-xl md:text-2xl">🗺️</span> How to Reach Us
              </h3>
              
              {/* Animated Route */}
              <div className="relative">
                {/* Route Line */}
                <div className="absolute left-5 md:left-6 top-6 md:top-8 bottom-6 md:bottom-8 w-1 bg-gradient-to-b from-green-400 via-yellow-400 to-cyan-400 rounded-full animate-pulse"></div>
                
                {/* Landmarks - Reversed Order */}
                <div className="space-y-4 md:space-y-6">
                  {/* Start Point - MINDSPACE */}
                  <div className="flex items-center gap-3 md:gap-4 relative">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center z-10 shadow-lg animate-bounce flex-shrink-0" style={{animationDuration: '2s'}}>
                      <span className="text-lg md:text-xl">📚</span>
                    </div>
                    <div className="flex-1 bg-green-50 p-2 md:p-3 rounded-xl border-2 border-green-200">
                      <p className="font-bold text-green-700 text-sm md:text-base">🎯 MINDSPACE Reading Room</p>
                      <p className="text-xs md:text-sm text-green-600">Ground Floor, 4-81, Satyasaiangar</p>
                    </div>
                  </div>

                  {/* Landmark - Kesavayana Gunta */}
                  <div className="flex items-center gap-3 md:gap-4 relative">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-500 rounded-full flex items-center justify-center z-10 shadow-lg animate-bounce flex-shrink-0" style={{animationDuration: '2.5s', animationDelay: '0.5s'}}>
                      <span className="text-lg md:text-xl">🏘️</span>
                    </div>
                    <div className="flex-1 bg-yellow-50 p-2 md:p-3 rounded-xl border-2 border-yellow-200">
                      <p className="font-bold text-yellow-700 text-sm md:text-base">🏘️ Near: Kesavayana Gunta</p>
                      <p className="text-xs md:text-sm text-yellow-600">200m from reading room</p>
                    </div>
                  </div>

                  {/* Via Point - Satya Sai Mandir */}
                  <div className="flex items-center gap-3 md:gap-4 relative">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-pink-500 rounded-full flex items-center justify-center z-10 shadow-lg animate-bounce flex-shrink-0" style={{animationDuration: '3s', animationDelay: '1s'}}>
                      <span className="text-lg md:text-xl">🙏</span>
                    </div>
                    <div className="flex-1 bg-pink-50 p-2 md:p-3 rounded-xl border-2 border-pink-200">
                      <p className="font-bold text-pink-700 text-sm md:text-base">⛪ Landmark: Satya Sai Mandir</p>
                      <p className="text-xs md:text-sm text-pink-600">Turn right after the temple</p>
                    </div>
                  </div>

                  {/* Reference Point - Anantha College */}
                  <div className="flex items-center gap-3 md:gap-4 relative">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-cyan-500 rounded-full flex items-center justify-center z-10 shadow-lg animate-bounce flex-shrink-0" style={{animationDuration: '2s', animationDelay: '1.5s'}}>
                      <GraduationCap className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="flex-1 bg-cyan-50 p-2 md:p-3 rounded-xl border-2 border-cyan-200">
                      <p className="font-bold text-cyan-700 text-sm md:text-base">📍 From: Anantha College</p>
                      <p className="text-xs md:text-sm text-cyan-600">Head towards Padmavati Nagar</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps Button */}
              <a 
                href="https://maps.app.goo.gl/C1snkZEMp68WfoyKA" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block mt-4"
              >
                <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-xl py-3 font-bold gap-2 text-sm md:text-base">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5" />
                  Open in Google Maps 🗺️
                </Button>
              </a>
            </div>

            {/* Map iframe */}
            <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white/30 h-[250px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3877.2!2d79.42778!3d13.63056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a4d4b955e0c7c45%3A0x5cb3f6a68f7d3c7!2sJC7H%2BF6%20Tirupati%2C%20Andhra%20Pradesh%20517503!5e0!3m2!1sen!2sin!4v1704067200000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MINDSPACE Reading Room - JC7H+F6 Tirupati"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Success Stories Section - Indian Rags to Riches
const SuccessStoriesSection = () => {
  const [activeStory, setActiveStory] = useState(0);

  return (
    <section id="success-stories" className="py-16 md:py-24 bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-200 text-yellow-800 rounded-full text-sm font-bold mb-4">
            <Trophy className="w-5 h-5" />
            Inspiring Success Stories
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            From <span className="text-pink-500">Humble Beginnings</span> to Greatness! 🌟
          </h2>
          <p className="text-lg text-indigo-600 max-w-3xl mx-auto font-medium">
            These incredible Indians proved that education and reading can transform anyone's destiny. 
            Your journey to success starts with a book! 📚
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Story Cards with Photos */}
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 gap-3 md:gap-4">
            {successStories.map((story, index) => (
              <Card 
                key={story.id}
                onClick={() => setActiveStory(index)}
                className={`cursor-pointer transition-all duration-300 rounded-xl md:rounded-2xl border-2 md:border-3 overflow-hidden ${
                  activeStory === index 
                    ? 'border-pink-400 shadow-xl scale-[1.02] bg-white' 
                    : 'border-indigo-100 hover:border-pink-200 bg-white/80'
                }`}
              >
                <CardContent className="p-2 md:p-4">
                  <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-4">
                    <div className="relative">
                      <div className="w-14 h-14 md:w-20 md:h-20 rounded-lg md:rounded-xl overflow-hidden border-2 md:border-4 border-amber-200 shadow-lg" style={{
                        filter: activeStory === index ? 'none' : 'sepia(30%)',
                        transition: 'filter 0.3s ease'
                      }}>
                        <img 
                          src={story.image} 
                          alt={story.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      {activeStory === index && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="font-bold text-indigo-900 text-xs md:text-sm leading-tight">{story.name}</h3>
                      <p className="text-[10px] md:text-xs text-pink-500 font-semibold mt-0.5 md:mt-1 hidden sm:block">{story.title}</p>
                      <p className="text-[10px] md:text-xs text-indigo-400 mt-0.5 md:mt-1 hidden md:block">{story.achievement}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Active Story Detail */}
          <div className="lg:col-span-1">
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-3xl border-0 shadow-2xl h-full overflow-hidden">
              <CardContent className="p-0 flex flex-col h-full">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={successStories[activeStory].image}
                    alt={successStories[activeStory].name}
                    className="w-full h-full object-cover object-top"
                    style={{ filter: 'sepia(20%) contrast(1.1)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-600 via-indigo-600/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white drop-shadow-lg">{successStories[activeStory].name}</h3>
                    <p className="text-yellow-300 text-sm font-semibold">{successStories[activeStory].achievement}</p>
                  </div>
                </div>
                
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-indigo-100 text-sm leading-relaxed flex-1">
                    {successStories[activeStory].story}
                  </p>
                  
                  <div className="mt-4 p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    <Quote className="w-5 h-5 text-yellow-300 mb-2" />
                    <p className="text-yellow-200 italic text-sm font-medium">
                      "{successStories[activeStory].quote}"
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-10 text-center p-6 bg-white rounded-2xl shadow-lg border-2 border-yellow-200">
          <p className="text-indigo-700 font-bold text-lg">
            📖 These legends started just like you - with a dream and a book. 
            <span className="text-pink-500"> Your story begins at MINDSPACE!</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// Reading Articles Section
const ReadingArticlesSection = () => {
  return (
    <section id="articles" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-bold mb-4">
            <BookOpen className="w-5 h-5" />
            Knowledge Hub
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            How Reading <span className="text-pink-500">Transforms Lives</span> 📖
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Scientific research proves that reading is the most powerful tool for personal growth!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {readingArticles.map((article, index) => {
            const colors = [
              "from-pink-400 to-rose-500",
              "from-cyan-400 to-blue-500",
              "from-yellow-400 to-orange-500",
              "from-green-400 to-emerald-500"
            ];
            return (
              <Card key={article.id} className="group hover:shadow-xl transition-all duration-300 rounded-3xl border-3 border-indigo-100 bg-white overflow-hidden">
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-r ${colors[index]} p-4 flex items-center gap-3`}>
                    <span className="text-4xl">{article.emoji}</span>
                    <div>
                      <span className="text-white/80 text-xs font-bold uppercase tracking-wide">{article.category}</span>
                      <h3 className="text-lg font-bold text-white">{article.title}</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-indigo-700 mb-4 font-medium">{article.summary}</p>
                    <ul className="space-y-2">
                      {article.keyPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                          <span className="text-sm text-indigo-600">{point}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-pink-500 font-semibold mt-4">📊 Source: {article.source}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Study Techniques Section
const StudyTechniquesSection = () => {
  const [activeTechnique, setActiveTechnique] = useState(0);

  return (
    <section id="techniques" className="py-16 md:py-24 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-200 text-purple-700 rounded-full text-sm font-bold mb-4">
            <Brain className="w-5 h-5" />
            Science-Backed Methods
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            Powerful <span className="text-pink-500">Study Techniques</span> 🧠
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Master these psychology-backed methods used by top students and researchers worldwide!
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Technique Tabs */}
          <div className="space-y-3">
            {studyTechniques.map((technique, index) => (
              <button
                key={technique.id}
                onClick={() => setActiveTechnique(index)}
                className={`w-full text-left p-4 rounded-2xl transition-all duration-300 ${
                  activeTechnique === index
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg'
                    : 'bg-white hover:bg-indigo-50 text-indigo-700 border-2 border-indigo-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{technique.icon}</span>
                  <span className="font-bold">{technique.name}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Active Technique Detail */}
          <div className="lg:col-span-2">
            <Card className="bg-white rounded-3xl border-3 border-purple-200 shadow-xl h-full">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center text-3xl shadow-lg">
                    {studyTechniques[activeTechnique].icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-indigo-900">{studyTechniques[activeTechnique].name}</h3>
                    <p className="text-pink-500 font-semibold">By {studyTechniques[activeTechnique].inventor}</p>
                  </div>
                </div>

                <p className="text-indigo-700 text-lg mb-6 leading-relaxed">
                  {studyTechniques[activeTechnique].description}
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {studyTechniques[activeTechnique].benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-green-50 p-3 rounded-xl">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-indigo-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-5 h-5 text-yellow-600" />
                    <span className="font-bold text-yellow-800">How to Apply:</span>
                  </div>
                  <p className="text-yellow-700 font-medium">{studyTechniques[activeTechnique].howTo}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Thinkers Quotes Section
const ThinkersQuotesSection = () => {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Wisdom from <span className="text-yellow-300">Great Thinkers</span> 💭
          </h2>
          <p className="text-lg text-pink-100 max-w-2xl mx-auto font-medium">
            Timeless words that continue to inspire millions!
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {thinkerQuotes.map((thinker) => (
            <Card key={thinker.id} className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-3xl hover:bg-white/20 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="text-5xl mb-4">{thinker.image}</div>
                <Quote className="w-8 h-8 text-yellow-300 mx-auto mb-3" />
                <p className="text-white italic text-lg mb-4 leading-relaxed">"{thinker.quote}"</p>
                <p className="text-yellow-300 font-bold">— {thinker.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reading Pathways Section
const ReadingPathwaysSection = () => {
  return (
    <section id="pathways" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-bold mb-4">
            <Target className="w-5 h-5" />
            Your Reading Journey
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            Pathways to <span className="text-pink-500">Effective Reading</span> 🛤️
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Follow these steps to become a more effective reader and learner!
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {readingPathways.map((pathway, index) => (
            <Card key={pathway.id} className="group hover:shadow-xl transition-all duration-300 rounded-3xl border-3 border-indigo-100 bg-gradient-to-br from-white to-indigo-50 overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform">
                    {pathway.icon}
                  </div>
                  <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center font-bold text-pink-500">
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-indigo-900 mb-2">{pathway.title}</h3>
                <p className="text-indigo-600 mb-4">{pathway.description}</p>
                <div className="bg-yellow-50 p-3 rounded-xl">
                  <p className="text-sm text-yellow-700 font-medium">
                    💡 <strong>Pro Tip:</strong> {pathway.tip}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Study Tools Section - BreatheFlow
const StudyToolsSection = () => {
  const tool = studyTools[0]; // BreatheFlow
  
  return (
    <section id="tools" className="py-16 md:py-24 bg-gradient-to-br from-cyan-50 via-teal-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-200 text-teal-700 rounded-full text-sm font-bold mb-4">
            <Sparkles className="w-5 h-5" />
            Recommended Tool
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">
            Boost Your <span className="text-teal-500">Focus & Calm</span> 🧘
          </h2>
          <p className="text-lg text-indigo-600 max-w-2xl mx-auto font-medium">
            Before you dive into studying, take a moment to calm your mind!
          </p>
        </div>

        <Card className="max-w-4xl mx-auto bg-white rounded-3xl border-4 border-teal-200 shadow-2xl overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2">
              {/* Left side - Info */}
              <div className="p-8 bg-gradient-to-br from-cyan-500 via-teal-500 to-green-500 text-white">
                <div className="text-6xl mb-4">{tool.icon}</div>
                <h3 className="text-3xl font-bold mb-2">{tool.name}</h3>
                <p className="text-teal-100 text-lg font-medium mb-4">{tool.tagline}</p>
                <p className="text-white/90 mb-6 leading-relaxed">
                  {tool.description}
                </p>
                <a 
                  href={tool.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50 rounded-full px-8 font-bold shadow-lg gap-2">
                    <span>Try BreatheFlow Free</span>
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
              </div>

              {/* Right side - Features */}
              <div className="p-8">
                <h4 className="text-xl font-bold text-indigo-900 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-teal-500" />
                  Features
                </h4>
                <ul className="space-y-3 mb-6">
                  {tool.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4 text-teal-600" />
                      </div>
                      <span className="text-indigo-700 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-5 h-5 text-yellow-600" />
                    <span className="font-bold text-yellow-800">How to Use:</span>
                  </div>
                  <p className="text-yellow-700 font-medium text-sm">{tool.benefit}</p>
                </div>

                <div className="mt-6 p-4 bg-indigo-50 rounded-2xl">
                  <p className="text-indigo-600 text-sm font-medium">
                    🔬 <strong>Science Fact:</strong> Deep breathing activates your parasympathetic nervous system, 
                    reducing cortisol (stress hormone) by up to 25% and improving memory retention!
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick breathing tip */}
        <div className="mt-8 text-center">
          <p className="text-indigo-600 font-medium">
            💨 <strong>Quick Tip:</strong> Try the 4-7-8 technique: Breathe in for 4 seconds, hold for 7, exhale for 8. 
            Repeat 3 times before studying!
          </p>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-indigo-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">MINDSPACE</span>
                <p className="text-xs text-pink-300 font-semibold">Reading Room ✨</p>
              </div>
            </div>
            <p className="text-indigo-200 font-medium">
              Your perfect study destination in Tirupati! 
              Achieve your dreams with us! 🌟
            </p>
          </div>

          <div>
            <h4 className="font-bold text-yellow-300 mb-4 text-lg">Quick Links 🔗</h4>
            <ul className="space-y-2">
              <li><a href="#about" className="text-indigo-200 hover:text-pink-300 transition-colors font-medium">About Us</a></li>
              <li><a href="#facilities" className="text-indigo-200 hover:text-pink-300 transition-colors font-medium">Facilities</a></li>
              <li><a href="#pricing" className="text-indigo-200 hover:text-pink-300 transition-colors font-medium">Pricing</a></li>
              <li><a href="#contact" className="text-indigo-200 hover:text-pink-300 transition-colors font-medium">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-yellow-300 mb-4 text-lg">Contact Info 📞</h4>
            <ul className="space-y-3 text-indigo-200">
              <li className="flex items-center gap-2 font-medium">
                <Phone className="w-5 h-5 text-pink-400" />
                {businessInfo.phone}
              </li>
              <li className="flex items-start gap-2 font-medium">
                <MapPin className="w-5 h-5 text-pink-400 mt-1" />
                <span>{businessInfo.address.city}, {businessInfo.address.state}</span>
              </li>
              <li className="flex items-center gap-2 font-medium">
                <Clock className="w-5 h-5 text-pink-400" />
                {businessInfo.hours.open} - {businessInfo.hours.close}
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-indigo-700 mt-12 pt-8 text-center">
          <p className="text-indigo-300 font-medium">
            Made with <Heart className="w-4 h-4 inline text-pink-400 fill-pink-400" /> © {new Date().getFullYear()} MINDSPACE Reading Room
          </p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  return (
    <div className="min-h-screen pb-16 md:pb-0">
      <Header />
      <HeroSection />
      <StatsSection />
      <AboutSection />
      <FeaturedShowcaseSection />
      <FacilitiesSection />
      <GallerySection />
      <PricingSection />
      <SuccessStoriesSection />
      <ReadingArticlesSection />
      <StudyTechniquesSection />
      <StudyToolsSection />
      <ReadingPathwaysSection />
      <ThinkersQuotesSection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
      
      {/* Mobile Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-indigo-200 p-3 flex gap-2 md:hidden z-50 shadow-2xl">
        <a href={`tel:${businessInfo.phone}`} className="flex-1">
          <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl py-3 font-bold gap-1 text-xs">
            <Phone className="w-4 h-4" />
            Call
          </Button>
        </a>
        <a href="/register" className="flex-1">
          <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl py-3 font-bold gap-1 text-xs">
            📝 Register
          </Button>
        </a>
        <a href={`https://wa.me/91${businessInfo.whatsapp}?text=Hi!%20I%20want%20to%20book%20a%20seat%20at%20MINDSPACE%20Reading%20Room`} target="_blank" rel="noopener noreferrer" className="flex-1">
          <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl py-3 font-bold gap-1 text-xs">
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </Button>
        </a>
      </div>
    </div>
  );
};

export default LandingPage;
